import { MutableRefObject, useState } from "react";
import { useCreateDCAOrder } from "api/dca/useCreateDCAOrder";
import { useGetOpenPortfolioHoldingDetails } from "api/holdings/useGetOpenPortfolioHoldingDetails";
import { useGetPortfolioHoldingDetails } from "api/holdings/useGetPortfolioHoldingDetails";
import { useGetStakingHoldingDetails } from "api/holdings/useStakingDetails";
import useCryptoMinMax from "api/minmax/useFetchMinMax";
import useFetchQuotes from "api/prices/useFetchPrices";
import { useGetBuyDataWithOpenOrders } from "api/trading/useGetBuyDataWithOpenOrderStatus";
import { useGetTradebleSecurities } from "api/trading/useGetTradebleSecurities";
import { Button, Input, LoadingIndicator } from "components";
import Alert from "components/Alert";
import { Select } from "components/Select/Select";
import { Tabs } from "components/Tabs";
import { useModifiedTranslation } from "hooks/useModifiedTranslation";
import {
  SuccessfulTradeResponse,
  useTradeExecution,
} from "hooks/useTradeExecution";
import { useTradingForm, OrderType } from "hooks/useTradingForm";
import { FiLoader } from "react-icons/fi";
import { HiSwitchVertical } from "react-icons/hi";
import { IoMdCheckmarkCircleOutline } from "react-icons/io";
import { toast } from "react-toastify";
import { useFilterSecuritiesByPortfolioCurrency } from "views/trading/hooks/useFilterSecuritiesByPortfolioCurrency";
import { useTradingFees } from "views/trading/TradingSummary";

import { getDecimalPlaces } from "views/trading/utils";
import {
  PortfolioSelect,
  usePortfolioSelect,
} from "views/transfers/components/PortfolioSelect";
import SecuritySelect, {
  useSecuritySelect,
} from "views/transfers/components/SecuritySelect";
import TransactionDetails from "./TransactionDetails";

interface BuySellModalProps {
  securityId?: number;
  modalInitialFocusRef: MutableRefObject<null>;
  modalInitialDataRef?: MutableRefObject<null>;
  onClose: () => void;
  onTradeCompletion?: () => void;
  initialOperation?: "buy" | "sell";
}

export const handleLongInput = (value: string, maxLength: number): boolean => {
  return value.length > maxLength;
};

// Helper function to truncate text with ellipsis
const truncateWithEllipsis = (
  text: string | undefined,
  maxLength: number = 6
): string => {
  if (!text) return "";
  return text.length > maxLength ? `${text.slice(0, maxLength)}..` : text;
};

// Define an extended order type that includes DCA
type ExtendedOrderType = OrderType | "DCA";

export const BuySellModalContent = ({
  securityId,
  onClose,
  onTradeCompletion,
  initialOperation = "buy",
}: BuySellModalProps) => {
  const [txData, setTxData] = useState<SuccessfulTradeResponse | null>(null);
  const [tradeRejected, setTradeRejected] = useState(false);
  const [completedOrderType, setCompletedOrderType] =
    useState<ExtendedOrderType | null>(null);
  const [completedLimitPrice, setCompletedLimitPrice] = useState<string | null>(
    null
  );
  const [isDCAOrder, setIsDCAOrder] = useState(false);
  const [dcaCompleted, setDcaCompleted] = useState(false);

  const { t } = useModifiedTranslation();

  const { data: allSecurities, loading: securitiesLoading } =
    useGetTradebleSecurities();

  const [securities] = useFilterSecuritiesByPortfolioCurrency(
    allSecurities || []
  );

  const {
    selectedSecurityOpt,
    setSelectedSecurityOpt,
    securityOptions,
    selectedSecurity,
  } = useSecuritySelect(securities, {
    initialSecurityId: securityId,
  });

  const {
    portfolioId,
    setPortfolioId,
    portfolioOptions,
    selectedPortfolioOpt,
    selectedPortfolio,
    refetchQueries,
  } = usePortfolioSelect({
    showSecurityBalance: selectedSecurity,
  });

  const quotesData = useFetchQuotes(
    (selectedPortfolio?.currency.securityCode as "EUR" | "SEK") || "EUR"
  );
  const minMaxData = useCryptoMinMax();

  const {
    loading: loadingBuyData,
    data: { availableCash: availableCashAdjusted = 0 } = {},
    refetch: refetchBuyData,
  } = useGetBuyDataWithOpenOrders(selectedPortfolio?.id.toString());

  const {
    data: { amount: holdingAmount = 0 } = {},
    refetch: refetchPortfolio,
  } = useGetPortfolioHoldingDetails(
    selectedPortfolio?.id.toString(),
    selectedSecurity?.id.toString(),
    false
  );

  const { totalOpenSellOrders } = useGetOpenPortfolioHoldingDetails(
    selectedPortfolio?.id.toString(),
    selectedSecurity?.securityCode
  );

  const { totalStakeAmount } = useGetStakingHoldingDetails(
    selectedPortfolio?.id.toString(),
    selectedSecurity?.securityCode
  );

  const availableHoldingAmount =
    holdingAmount - totalOpenSellOrders - totalStakeAmount;

  const {
    amount,
    inputError,
    tradeType,
    orderType,
    actionType,
    setTradeType,
    handleAmountChange,
    handleLimitOrderAmountChange,
    resetForm,
    limitOrderPrice,
    unitPriceWithSlippage,
    unitPrice,
    endTime,
    handleBuySellChange,
    handleLimitOrderPriceChange,
    isLimitOnly,
    totalToShow,
    triggerPrice,
    handleOrderTypeChange,
  } = useTradingForm({
    availableCash: availableCashAdjusted || 0,
    availableHoldingAmount: availableHoldingAmount || 0,
    securityName: selectedSecurity?.name || "",
    securityCode: selectedSecurity?.securityCode || "",
    minMaxData,
    quotesData,
    initialOperation,
    defaultToMarket: true,
  });

  const { tentativeFee } = useTradingFees({
    portfolioId: selectedPortfolio?.id.toString() || "",
    portfolioCurrency: selectedPortfolio?.currency.securityCode || "",
    amount,
    limitOrderPrice,
    orderType,
    unitPrice,
    tradeType,
  });

  const tradeExecution = useTradeExecution({
    portfolioShortName: selectedPortfolio?.shortName || "",
    contactId: portfolioId?.toString() || "",
    securityCode: selectedSecurity?.securityCode || "",
    currency: selectedSecurity?.currency?.securityCode || "",
    amount,
    limitOrderPrice,
    unitPriceWithSlippage,
    orderType,
    tradeType,
    endTime,
    actionType,
    triggerPrice,
  });

  const { createDCAOrder, isLoading: isCreatingDCA } = useCreateDCAOrder();

  const handleSuccessfulTrade = (txData: SuccessfulTradeResponse) => {
    setCompletedOrderType(isDCAOrder ? "DCA" : orderType);
    setCompletedLimitPrice(limitOrderPrice);

    resetForm();
    refetchQueries();
    refetchBuyData();
    refetchPortfolio();
    if (onTradeCompletion) {
      onTradeCompletion();
    }
    setTxData(txData);
  };

  const handleSubmit = async () => {
    setTradeRejected(false);

    if (!selectedSecurity || !selectedPortfolio) {
      return;
    }

    // Handle DCA order creation
    if (isDCAOrder) {
      try {
        const dcaPayload = {
          units: parseFloat(amount),
          securityCode: selectedSecurity.securityCode,
          currency: selectedPortfolio.currency.securityCode,
          contact: portfolioId?.toString() || "",
          portfolioShortName: selectedPortfolio.shortName,
          buyTime: 7200000, // Fixed time: 5th at 02:00 UTC
          ...(tradeType === "amount" && { unitAsset: "quote" }),
        };

        await createDCAOrder(dcaPayload);

        // Show DCA success screen
        setDcaCompleted(true);

        // Reset form and notify success
        resetForm();
        refetchQueries();
        refetchBuyData();
        if (onTradeCompletion) {
          onTradeCompletion();
        }
      } catch (error) {
        console.error("DCA order creation failed:", error);
      }
      return;
    }

    // Handle regular trade execution
    const result = await tradeExecution.executeTrade();

    if (result.status === "executed") {
      handleSuccessfulTrade(result);
    } else if (result.status === "accepted") {
      handleSuccessfulTrade(result);
    } else {
      toast.error(t("messages.error"));
      setTradeRejected(true);
    }
  };

  if (dcaCompleted) {
    return (
      <div className="w-full sm:w-[450px]">
        <div className="flex flex-col p-1 m-4 sm:m-0 h-full bg-white sm:bg-success rounded-3xl shadow-sm sm:shadow-none">
          <div className="flex flex-col gap-2 items-center sm:items-start py-3 sm:py-2 px-4 m-2 sm:m-0 text-xl sm:text-2xl font-bold text-center sm:text-left text-black sm:text-white bg-success sm:bg-opacity-100 rounded-3xl bg-opacity-15">
            <IoMdCheckmarkCircleOutline className="sm:hidden w-10 h-10 fill-success" />
            {t("tradingModal.dcaOrderCreated")}
          </div>
          <div className="flex flex-col px-6 pt-3 pb-4 text-kvarn-dark-green bg-white rounded-b-3xl">
            <div className="py-4 text-center">
              <p className="mb-2 text-lg">
                {t("tradingModal.dcaOrderCreatedDescription")}
              </p>
              <p className="text-sm opacity-80">
                {t("tradingModal.dcaScheduleDescription")}
              </p>
            </div>
            <Button className="mt-4" onClick={onClose} variant="Primary">
              {t("common.continue")}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (txData) {
    return (
      <div className="w-full sm:w-[450px]">
        <TransactionDetails
          txData={txData}
          type={actionType}
          orderType={
            completedOrderType === "DCA"
              ? undefined
              : completedOrderType ?? undefined
          }
          limitPrice={completedLimitPrice ?? undefined}
        />
      </div>
    );
  }

  const loading = securitiesLoading || loadingBuyData;

  if (loading) {
    return (
      <div className="flex justify-center items-center py-8 px-4 w-full h-full sm:bg-white sm:rounded-3xl min-h-72 sm:w-[450px]">
        <LoadingIndicator />
      </div>
    );
  }

  const orderTypeOptions = isLimitOnly
    ? [{ id: "Limit", label: t("tradingWidget.limitOrder") }]
    : [
        { id: "Market", label: t("tradingWidget.marketOrder") },
        { id: "Limit", label: t("tradingWidget.limitOrder") },
        { id: "DCA", label: t("tradingWidget.dcaOrder") },
      ];

  const selectedOrderTypeOpt = isDCAOrder
    ? { id: "DCA", label: t("tradingWidget.dcaOrder") }
    : orderTypeOptions.find((opt) => opt.id === orderType) || {
        id: "Market",
        label: t("tradingWidget.marketOrder"),
      };

  return (
    <div className="flex flex-col w-full h-full sm:h-auto text-kvarn-dark-green sm:w-[450px]">
      <div className="flex gap-2 justify-between items-center p-4 sm:px-6">
        {!isDCAOrder && (
          <Tabs
            options={[
              { id: "buy", label: t("tradingModal.buy") },
              { id: "sell", label: t("tradingModal.sell") },
            ]}
            selectedTab={actionType}
            setSelectedTab={(id) => {
              handleBuySellChange(id as "buy" | "sell");
            }}
            variant="secondary"
          />
        )}
        <div className={isDCAOrder ? "w-full" : "w-[190px]"}>
          <Select
            options={orderTypeOptions}
            value={selectedOrderTypeOpt}
            onChange={(selected) => {
              if (selected) {
                if (selected.id === "DCA") {
                  setIsDCAOrder(true);
                  handleBuySellChange("buy");
                  setTradeType("amount"); // Default to EUR-based for DCA orders
                } else {
                  setIsDCAOrder(false);
                  handleOrderTypeChange(selected.id as "Market" | "Limit");
                }
              }
            }}
            disabled={isLimitOnly}
            buttonClassName="text-sm rounded-3xl"
          />
        </div>
      </div>
      <div className="flex overflow-auto flex-col flex-1 gap-4 py-4 px-6 bg-white sm:rounded-b-3xl">
        <div>
          <div className="text-sm font-bold opacity-80">
            {t("tradingModal.portfolio")}
          </div>
          <PortfolioSelect
            value={selectedPortfolioOpt || { id: -1, label: <div>--</div> }}
            onChange={(newPortfolio) => {
              setPortfolioId(newPortfolio.id);
            }}
            options={portfolioOptions}
          />
        </div>
        <div>
          <SecuritySelect
            securityOptions={securityOptions}
            selectedSecurityOpt={selectedSecurityOpt}
            onSecurityChange={(value) => setSelectedSecurityOpt(value)}
          />
        </div>

        <div>
          <Input
            value={amount}
            disabled={tradeExecution.isSubmitting || isCreatingDCA}
            onChange={
              isDCAOrder ? handleLimitOrderAmountChange : handleAmountChange
            }
            className={
              "py-6 px-4 font-bold border !text-5xl h-[100px] border-[#DADADA] bg-[#D9D9D933]"
            }
            error={inputError}
            errorClassName="text-red-500 text-xs mt-1"
            endAdornment={
              amount.length <= 4 ? (
                <div className="pr-4 text-5xl text-gray-400">
                  {tradeType === "units"
                    ? truncateWithEllipsis(
                        selectedSecurity?.securityCode?.toUpperCase()
                      )
                    : truncateWithEllipsis(
                        selectedPortfolio?.currency.securityCode
                      )}
                </div>
              ) : null
            }
          />

          <div className="flex justify-between items-center mt-1">
            {orderType === "Market" && actionType === "buy" && !isDCAOrder && (
              <button
                disabled={isLimitOnly}
                className="flex gap-1 items-center text-sm font-semibold text-blue-600 hover:text-blue-800 transition-colors"
                onClick={() => {
                  resetForm();
                  setTradeType(tradeType === "units" ? "amount" : "units");
                }}
              >
                <HiSwitchVertical />
                <div>
                  {tradeType === "units"
                    ? truncateWithEllipsis(
                        selectedPortfolio?.currency.securityCode
                      )
                    : truncateWithEllipsis(
                        selectedSecurity?.securityCode?.toUpperCase()
                      )}
                </div>
              </button>
            )}
            <div
              className={`text-xs opacity-80 ${
                orderType === "Market" && actionType === "buy" && !isDCAOrder
                  ? ""
                  : "ml-auto"
              }`}
            >
              <span>{t("tradingModal.indicativeFee")}:</span>{" "}
              <span>
                {tentativeFee}{" "}
                {truncateWithEllipsis(
                  selectedPortfolio?.currency.securityCode,
                  8
                )}
              </span>
            </div>
          </div>
        </div>
        {orderType === "Limit" && !isDCAOrder && (
          <div>
            <div className="text-sm font-bold opacity-80">
              {t("tradingWidget.limitPrice")}
            </div>
            <Input
              value={limitOrderPrice}
              onChange={handleLimitOrderPriceChange}
              className="py-6 px-4 font-bold border !text-5xl h-[100px] border-[#DADADA] bg-[#D9D9D933]"
              disabled={tradeExecution.isSubmitting || isCreatingDCA}
              endAdornment={
                <div className="pr-4 text-5xl text-gray-400">
                  {truncateWithEllipsis(
                    selectedPortfolio?.currency.securityCode
                  )}
                </div>
              }
            />
          </div>
        )}
        {isDCAOrder && (
          <div className="p-4 bg-blue-50 rounded-lg">
            <div className="mb-1 text-sm font-bold text-blue-900">
              {t("tradingModal.dcaSchedule")}
            </div>
            <div className="text-sm text-blue-800">
              {t("tradingModal.dcaScheduleDescription")}
            </div>
          </div>
        )}
        <div className="flex flex-col gap-2 my-2">
          <div>
            <div className="opacity-80">{t("tradingModal.unitPrice")}</div>
            <div className="font-semibold">
              <span>
                {unitPrice
                  ? unitPrice.toFixed(getDecimalPlaces(unitPrice))
                  : "-"}
              </span>{" "}
              <span>
                {truncateWithEllipsis(
                  selectedPortfolio?.currency.securityCode,
                  8
                )}
              </span>
            </div>
          </div>
          <div>
            <div className="opacity-80">
              {tradeType === "units"
                ? t("tradingModal.tradeAmount")
                : t("tradingModal.unitsInputLabel")}
            </div>
            <div className="font-semibold">
              {totalToShow}{" "}
              {tradeType === "units"
                ? truncateWithEllipsis(
                    selectedPortfolio?.currency.securityCode,
                    8
                  )
                : truncateWithEllipsis(
                    selectedSecurity?.securityCode?.toUpperCase(),
                    8
                  )}
            </div>
          </div>
        </div>
        <Button
          className="py-2 mt-auto !text-2xl !font-bold"
          onClick={handleSubmit}
          variant={
            isDCAOrder ? "Primary" : actionType === "buy" ? "Primary" : "Red"
          }
          disabled={
            !!inputError ||
            !amount ||
            amount === "0" ||
            tradeExecution.isSubmitting ||
            isCreatingDCA ||
            !selectedSecurity ||
            !selectedPortfolio ||
            (!isDCAOrder && orderType === "Limit" && !limitOrderPrice)
          }
        >
          {tradeExecution.isSubmitting || isCreatingDCA ? (
            <>
              <FiLoader className="mr-2 animate-spin" />
              {t("tradingModal.submitting")}
            </>
          ) : isDCAOrder ? (
            t("tradingModal.confirmDCA")
          ) : actionType === "buy" ? (
            t("tradingModal.confirmBuy")
          ) : (
            t("tradingModal.confirmSell")
          )}
        </Button>
        {tradeRejected && (
          <Alert
            className="block sm:hidden"
            variant="warning"
            content={
              <div className="flex flex-col gap-1">
                <div className="font-bold">
                  {t("tradingModal.orderRejected")}
                </div>
                <div>{t("tradingModal.priceMovement")}</div>
              </div>
            }
          />
        )}
        <div className="text-xs opacity-80">
          {t("tradingModal.buyDisclaimer")}
        </div>
      </div>
    </div>
  );
};
